import { useState, useEffect } from "react";
import { Clock, X, Mail } from "lucide-react";
import { useTrialStatus, TrialUrgency } from "@/hooks/useTrialStatus";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const DISMISS_KEY = "trial-banner-dismissed";

function getBannerStyles(urgency: TrialUrgency): string {
  switch (urgency) {
    case "info":
      return "bg-muted/50 text-muted-foreground border-b border-border/50";
    case "warning":
      return "bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 border-b border-amber-200/50 dark:border-amber-800/30";
    case "urgent":
      return "bg-rose-50 dark:bg-rose-950/20 text-rose-800 dark:text-rose-200 border-b border-rose-200/50 dark:border-rose-800/30";
    default:
      return "";
  }
}

function getIconStyles(urgency: TrialUrgency): string {
  switch (urgency) {
    case "info":
      return "text-muted-foreground";
    case "warning":
      return "text-amber-600 dark:text-amber-400";
    case "urgent":
      return "text-rose-600 dark:text-rose-400";
    default:
      return "text-muted-foreground";
  }
}

export function TrialBanner() {
  const { t } = useLanguage();
  const { urgency, daysRemaining, hoursRemaining, isInTrial, isLoading } = useTrialStatus();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check session storage on mount
  useEffect(() => {
    const dismissed = sessionStorage.getItem(DISMISS_KEY);
    if (dismissed === "true") {
      setIsDismissed(true);
    }
  }, []);

  // Don't show if: loading, not in trial, no urgency, or dismissed
  if (isLoading || !isInTrial || urgency === "none" || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, "true");
  };

  const handleContact = () => {
    window.location.href = "mailto:support@orderflow.ge?subject=Trial%20Question";
  };

  // Determine message based on time remaining
  let message: string;
  if (daysRemaining !== null && daysRemaining > 0) {
    message = t("trial.banner.daysRemaining").replace("{{days}}", String(daysRemaining));
  } else if (hoursRemaining !== null && hoursRemaining > 0) {
    message = t("trial.banner.hoursRemaining").replace("{{hours}}", String(hoursRemaining));
  } else {
    message = t("trial.banner.endsToday");
  }

  return (
    <div className={cn(
      "flex items-center justify-between px-4 py-2 text-sm",
      getBannerStyles(urgency)
    )}>
      <div className="flex items-center gap-2 min-w-0">
        <Clock className={cn("h-4 w-4 shrink-0", getIconStyles(urgency))} />
        <span className="truncate">{message}</span>
      </div>

      <div className="flex items-center gap-2 shrink-0 ml-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleContact}
          className="h-7 text-xs gap-1.5 hover:bg-background/50"
        >
          <Mail className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{t("trial.banner.contactCta")}</span>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDismiss}
          className="h-7 w-7 hover:bg-background/50"
          aria-label={t("trial.banner.dismiss")}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
